import React from 'react';
import { MeatballMenu, ButtonCallToAction, ButtonTertiary, PromptConfirmationModal } from 'ui';
import { Link } from 'react-router-dom';
import { routeConstants } from 'app/routes/route-constants';
import { Tag } from 'app/components/tag';
import { isDisabled } from 'app/infrastructure/data/date';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';
import * as navigate from 'app/area/shared/navigation';
import { StickyHeader, StickyHeaderButtonContainer } from 'app/shell/components/sticky-header/sticky-header';
import { getBackRoute } from '../route';
import { CompanyReportStickyHeaderProps } from '../types';
import { useTemplateFileHandlers } from '../hooks';

import styles from './company-report-sticky-header.scss';
import { UploadDocument } from 'app/area/company-report/upload-document';
import { useOpenState } from 'app/infrastructure/hooks';
import { FileTypes } from 'app/infrastructure/remote-data';
import { replaceTemplateString } from 'app/infrastructure/platform';

export const CompanyReportStickyHeader: React.FC<CompanyReportStickyHeaderProps> = ({
  companyReportItem, name, shouldPrompt, fetching, fetchingTemplate, translation, companyReportAccess,
  displayError, onSave, onDelete
}) => {

  const disabled = isDisabled(companyReportItem.disabled);

  const updatingItem = isDefinedIncludingZero(companyReportItem?.id);

  const onCancel = () => {
    navigate.to({ pathname: getBackRoute() });
  };

  const {
    isOpen: showImportDocumentModal,
    onOpen: handleAddTemplate,
    onClose
  } = useOpenState();

  const {
    handleUploadTemplate,
    handleDownloadTemplate
  } = useTemplateFileHandlers(companyReportItem?.id, translation, companyReportAccess, onClose);

  return <>
    <StickyHeader
      className={styles.header}
      rightSideContent={<>
        <StickyHeaderButtonContainer>
          <ButtonTertiary
            id="company-report-form-sticky-header-cancel-button"
            onClick={onCancel}>
            {translation.cancel}
          </ButtonTertiary>
          <ButtonCallToAction
            id="company-report-form-sticky-header-save-button"
            onClick={onSave}
            disabled={!shouldPrompt}>
            {translation.save}
          </ButtonCallToAction>
        </StickyHeaderButtonContainer>
        {updatingItem && <MeatballMenu id="company-report-form-sticky-header-meatballmenu">
          {!disabled && <li>
            <Link className="dropdown-item" to={routeConstants.companyReportFor(companyReportItem.id)}>
              {translation.run_company_report}
            </Link>
          </li>}
          <li>
            <button type="button" className="dropdown-item"
              onClick={companyReportAccess.manageReportTemplates
                ? handleAddTemplate :
                () => displayError(translation.no_upload_permission)}>
              {translation.template_upload}
            </button>
          </li>
          <li>
            <button disabled={fetchingTemplate} type="button" className="dropdown-item" onClick={handleDownloadTemplate}>
              {translation.template_download}
            </button>
          </li>
          <li>
            <PromptConfirmationModal
              title={translation.delete_company_report_title}
              description={replaceTemplateString(
                translation.delete_confirmation,
                name)}
              continueText={translation.delete_company_report_button}
              cancelText={translation.cancel}
              onContinue={onDelete}>
              <button type="button" className="dropdown-item" disabled={fetching}>
                {translation.delete_company_report_title}
              </button>
            </PromptConfirmationModal>
          </li>
        </MeatballMenu>}
      </>} >
      <div className={styles.headerContainer}>
        {updatingItem ? <span className={styles.headerId}>
          #{companyReportItem.id}
        </span> : null}
        <h2 className={styles.headerTitle}>
          {updatingItem
            ? name || null
            : translation.new_company_report}
        </h2>
        {disabled ? <Tag>{translation.disabled}</Tag> : null}
      </div>

    </StickyHeader>
    {showImportDocumentModal && <UploadDocument
      onSubmit={handleUploadTemplate}
      onClose={onClose}
      textCancel={translation.cancel}
      textSubmit={translation.submit}
      textChooseFile={translation.choose_file}
      textTitle={translation.upload_template}
      textFileTooLargeError={translation.file_size_error}
      acceptFileType={FileTypes.EXCEL}
    />}
  </>;
};
